import { useState, useMemo, useEffect } from 'react'
import {
  AuthPageProps,
  Button,
  colors,
  Form,
  FormErrors,
  radius,
  useToast,
  Text,
} from '../../index.js'
import { AuthContainer } from './AuthContainer.js'

function randomCode() {
  // prettier-ignore
  const adjs = ["Vibrant", "Radiant", "Sparkling", "Dazzling", "Brilliant", "Magnificent", "Joyful", "Blissful", "Euphoric", "Effervescent", "Bubbly", "Cheerful", "Sassy", "Fierce", "Fabulous", "Glamorous", "Chic", "Stylish", "Playful", "Whimsical", "Magical", "Divine", "Heavenly", "Celestial", "Stellar", "Cosmic", "Wonderful", "Fantastic", "Splendid", "Superb", "Groovy", "Funky", "Cool", "Awesome", "Amazing", "Lovely", "Charming", "Delightful", "Enchanting", "Glimmering", "Shimmering", "Glistening", "Luminous", "Gleaming", "Sunny", "Bright", "Glowing", "Zesty", "Lively", "Spirited", "Exuberant", "Jubilant", "Hopeful", "Optimistic", "Kind", "Sweet", "True", "Loyal", "Perfect", "Pristine", "Pure", "Bold", "Brave", "Flawless",]
  // prettier-ignore
  const nouns = ["Glitter", "Rainbow", "Stardust", "Magic", "Fantasy", "Wonder", "Joy", "Bliss", "Euphoria", "Celebration", "Festival", "Carnival", "Parade", "Party", "Disco", "Diva", "Queen", "King", "Legend", "Icon", "Hero", "Muse", "Starburst", "Sunbeam", "Moonbeam", "Comet", "Aurora", "Galaxy", "Cosmos", "Planet", "Bloom", "Blossom", "Petal", "Garden", "Meadow", "Oasis", "Haven", "Paradise", "Utopia", "Dreamland", "Wonderland", "Heaven", "Symphony", "Harmony", "Melody", "Anthem", "Serenade", "Sparkle", "Dazzle", "Shimmer", "Glimmer", "Halo", "Aura", "Embrace", "Hug", "Kiss", "Smile", "Giggle", "Laughter", "Glee", "Spark", "Flash", "Groove", "Radiance",]

  const randomNumbers = new Uint32Array(2)
  globalThis.crypto.getRandomValues(randomNumbers)

  return (
    adjs[randomNumbers[0] % adjs.length] +
    ' ' +
    nouns[randomNumbers[1] % nouns.length]
  )
}

function generateScreenCode() {
  return randomCode()
  // return (~~(Math.random() * 1e6)).toString(16)
}
export const MagicLinkLogin = ({
  appName,
  onSubmit,
  onCreateAccount,
  onResend,
}: Omit<AuthPageProps, 'type'>) => {
  const [page, setPage] = useState<'email' | 'code'>('email')
  const [submittedEmail, setSubmittedEmail] = useState('')
  const screenCode = useMemo(generateScreenCode, [])
  const [resendState, setResendState] = useState<'initial' | 'loading'>(
    'initial',
  )
  const [resendSeconds, setResendSeconds] = useState<number>(59)
  const toast = useToast()

  useEffect(() => {
    let intervalId: number

    if (resendState === 'loading') {
      intervalId = window.setInterval(() => {
        setResendSeconds((p) => p - 1)
      }, 1000)
    }

    return () => {
      window.clearInterval(intervalId)
    }
  }, [resendState])

  useEffect(() => {
    if (resendSeconds < 1) {
      setResendState('initial')
      setResendSeconds(59)
    }
  }, [resendSeconds])

  return (
    <AuthContainer title="Login to your account" subtitle={appName}>
      {page === 'email' && (
        <>
          <Form
            fields={{
              email: {
                type: 'text',
                label: 'Email',
                placeholder: 'Add your email',
              },
            }}
            onSubmit={async (values) => {
              try {
                // TODO: validate email
                const email = values['email'] as string
                setSubmittedEmail(email)
                setPage('code')
                await onSubmit?.({ email, code: screenCode })
              } catch (err) {
                setPage('email')
                console.error(err)
                toast('You cannot access this workspace', {
                  color: 'red',
                  icon: 'error-filled',
                  description:
                    'You might need an invitation from a workspace administrator first.',
                })
                throw err
              }
            }}
            validate={(values) => {
              const errors: FormErrors = {}

              if (!values?.email) {
                errors['email'] = 'Enter a valid email address.'
              }

              return errors
            }}
          >
            {({ submitForm, isSubmitting, isDisabledFromOutside }) => (
              <>
                <div style={{ padding: '0 6px', width: '100%' }}>
                  <Form.Fields />
                </div>
                <div style={{ padding: '0 6px' }}>
                  <Button
                    keyHintVisibility="none"
                    keyHint="Enter"
                    variant="fill"
                    trailIcon="arrow-right"
                    loading={isSubmitting}
                    onClick={() => {
                      submitForm()
                    }}
                  >
                    Send magic link
                  </Button>
                </div>
              </>
            )}
          </Form>
          {onCreateAccount && (
            <>
              {' '}
              <div
                style={{
                  background: colors.neutral10,
                  height: 1,
                  width: '100%',
                }}
              />
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ padding: '0 6px' }}>
                  <Text variant="subtext-medium" color="neutral60">
                    Don't have an account yet?
                  </Text>
                </div>
                <Button
                  size="small"
                  variant="ghost"
                  trailIcon="arrow-right"
                  onClick={onCreateAccount}
                >
                  Create account
                </Button>
              </div>
            </>
          )}
        </>
      )}
      {page === 'code' && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexDirection: 'column',
              padding: '0 6px',
            }}
          >
            <Text variant="display-regular" color="neutral60">
              We've just sent a login link to {submittedEmail}.
            </Text>
            <Text variant="display-regular" color="neutral60">
              Please make sure the email contains the following security code:
            </Text>
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: radius[8],
                border: `1px solid ${colors.neutral10}`,
                minHeight: 36,
              }}
            >
              <Text variant="display-medium" color="neutral80">
                {screenCode}
              </Text>
            </div>
            <div
              style={{
                background: colors.neutral10,
                height: 1,
                width: '100%',
              }}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ padding: '0 6px' }}>
                <Text variant="subtext-medium" color="neutral60">
                  Something wrong?
                </Text>
              </div>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Button
                  size="small"
                  variant="ghost"
                  onClick={() => {
                    setPage('email')
                  }}
                >
                  Back to login
                </Button>
                {onResend && (
                  <Button
                    size="small"
                    variant="ghost"
                    disabled={resendState === 'loading'}
                    onClick={async () => {
                      try {
                        await onResend?.({
                          email: submittedEmail,
                          code: screenCode,
                        })
                        setResendState('loading')
                      } catch {
                        // TODO show error
                      }
                    }}
                  >
                    Re-send
                    {resendState === 'loading' ? ` (${resendSeconds})` : ''}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </AuthContainer>
  )
}
